import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ANALYTICS_EVENTS, useTrack } from '@/lib/analytics';
import type { ChatAttachment, LibraryFile } from '@/services/DataService';

/** Where a "Ask about this file" was chosen: the open file's panel, or a row's or tile's menu. */
export type AskAboutFileSource = 'panel' | 'menu';

/**
 * What a new chat is opened with to start from files already uploaded: read
 * by HomePage off the navigation state and seeded into the empty thread's
 * composer as ready attachments.
 */
export interface PendingComposerAttachmentsState {
  pendingComposerAttachments: ChatAttachment[];
}

/**
 * The part of a library file a chat attachment is: the rest (size, uploader,
 * dates) has no place in the composer or the message.
 *
 * @param file - The library file.
 *
 * @returns The attachment.
 */
export const toChatAttachment = ({ id, mime_type, filename, size_bytes, url }: LibraryFile): ChatAttachment => ({
  id, mime_type, filename, size_bytes, url,
});

/**
 * Starts a new chat about one library file.
 *
 * Nothing is sent and nothing is uploaded again: the chat opens on its empty
 * state with the file already in the composer, as if it had just been
 * attached, and the first message is whatever the user types. Like a skill
 * pre-attached from the Skills page, the conversation is only created when
 * that message is sent.
 */
export const useAskAboutFile = () => {
  const navigate = useNavigate();
  const trackEvent = useTrack();

  return useCallback((file: LibraryFile, source: AskAboutFileSource) => {
    trackEvent(ANALYTICS_EVENTS.LIBRARY_FILE_ASKED_ABOUT, { file_type: file.mime_type, source });
    const state: PendingComposerAttachmentsState = { pendingComposerAttachments: [ toChatAttachment(file) ] };
    navigate('/ui/chat', { state });
  }, [ navigate, trackEvent ]);
};
